import FormMemberWrapper from "@domain/member/form-member-wrapper/form-member-wrapper";
import "./transaction-member.scss";
import { useTransactionStepContext } from "../transaction-step.context";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useNewTransactionContext } from "../new-transaction.context";
import { LabelValue } from "@shared/models/label-value.interface";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import ParentInfo, { ParentInfoMember } from "@core/ui/parent-info/parent-info";
import { Button } from "primereact/button";
import { useCallback, useEffect, useRef } from "react";
import { useNewSubscriptionContext } from "@domain/subscription/new-subscription.context";
import TextBlock from "@shared/ui/text-block/text-block";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { MemberFormMap } from "@core/model/member-form-map";

export interface TransactionMemberProps {
  contextType?: "transaction" | "subscription";
}

export function TransactionMember({
  contextType = "transaction",
}: TransactionMemberProps) {
  const {
    memberList,
    selectedMember,
    memberFormRef,
    memberFormData,
    handleMemberForm,
    setSelectedMember,
    handleMemberTrigger,
    nextStepper,
    previousStepper,
    isMemberListLoading,
  } = useTransactionStepContext();
  const { formatDate } = useDateTimeContext();

  const transactionContext = useNewTransactionContext();
  const subscriptionContext = useNewSubscriptionContext();

  const currentContext =
    contextType === "transaction" ? transactionContext : subscriptionContext;

  const {
    isCreating,
    memberInfo,
    isCreateNewMember,
    setIsCreateNewMember,
    setMemberInfo,
    setIsCreating,
    createMember,
  } = currentContext;

  const memberCreatedRef = useRef(false);

  const handleNewMember = () => {
    setIsCreateNewMember(true);
    memberCreatedRef.current = false;
  };

  const handleSelectMember = () => {
    setIsCreateNewMember(false);
    memberCreatedRef.current = false;
  };

  const handleMemberNext = useCallback(() => {
    if (!isCreateNewMember) {
      nextStepper();
      return;
    }

    if (isCreateNewMember && !memberCreatedRef.current) {
      handleMemberTrigger();
    }
  }, [isCreateNewMember, handleMemberTrigger, nextStepper]);

  useEffect(() => {
    if (isCreateNewMember && memberFormData && !memberCreatedRef.current) {
      memberCreatedRef.current = true;
      (async () => {
        await createMember();
        nextStepper();
      })();
    }
  }, [memberFormData, isCreateNewMember]);

  const handleMemberSelection = async (e: DropdownChangeEvent) => {
    setIsCreating(true);
    setSelectedMember(e.value);
    const filteredMember = memberList.find(
      (item) => item.value === e.value
    ) as LabelValue;

    if (!filteredMember) return;
    const memberPayload = filteredMember.payload;

    let fileBlob = undefined;
    if (memberPayload.avatar) {
      const elementFile = await viewAsset(memberPayload.avatar || "");
      fileBlob = URL.createObjectURL(elementFile);
    }

    setMemberInfo({ ...memberPayload, fileBlob });
    setIsCreating(false);
  };

  const isDisabledNext = useCallback(() => {
    const isSelectingMemberButNoData = !isCreateNewMember && !selectedMember;
    const isDisabled = isCreating || isSelectingMemberButNoData;
    return isDisabled;
  }, [isCreateNewMember, selectedMember, isCreating]);

  return (
    <div id="TransactionMember" className="transaction-member">
      <div className="flex flex-col h-12rem">
        <section className="flex gap-2 mb-4">
          {!isCreateNewMember ? (
            <Button
              label="Create New Member"
              outlined
              onClick={handleNewMember}
              severity="secondary"
            />
          ) : (
            <Button
              label="Select Member"
              outlined
              onClick={handleSelectMember}
              severity="secondary"
            />
          )}
        </section>
        <section className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          {isCreateNewMember ? (
            <FormMemberWrapper
              ref={memberFormRef}
              onSubmit={handleMemberForm}
            />
          ) : (
            <section>
              <div>
                <Dropdown
                  value={isMemberListLoading ? "" : selectedMember}
                  onChange={handleMemberSelection}
                  options={memberList || []}
                  placeholder={isMemberListLoading ? "Loading members list..." : "Select a member"}
                  filter
                  className="w-full"
                  disabled={isMemberListLoading}
                  loading={isMemberListLoading}
                />
              </div>

              {memberInfo ? (
                <ParentInfoMember model={MemberFormMap.Map(memberInfo)} />
              ) : null}
            </section>
          )}
        </section>
      </div>
      <div className="flex pt-4 justify-between">
        <Button
          label="Back"
          severity="success"
          outlined
          icon="pi pi-arrow-left"
          onClick={() => previousStepper()}
        />
        <Button
          label="Next"
          icon="pi pi-arrow-right"
          iconPos="right"
          disabled={isDisabledNext()}
          onClick={handleMemberNext}
        />
      </div>
    </div>
  );
}

export default TransactionMember;
